import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, PhoneCall, Plus, Sparkles, TrendingUp, UserPlus, X } from 'lucide-react';
import { getLeadAssignees, getLeadSummary, getLeads } from '../../api/leads';
import PageHead from '../../components/PageHead/PageHead';
import MetricCard from '../../components/MetricCard/MetricCard';
import SearchInput from '../../components/SearchInput/SearchInput';
import FilterSheet from '../../components/FilterSheet/FilterSheet';
import Dropdown from '../../components/Dropdown/Dropdown';
import TableCard from '../../components/TableCard/TableCard';
import Pagination from '../../components/Pagination/Pagination';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Avatar from '../../components/Avatar/Avatar';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import { useMediaQuery } from '../../components/Layout/shellState';
import { useI18n } from '../../i18n/I18nContext';
import { formatPhone, formatRelative } from '../../utils/datetime';
import { errorText } from '../../utils/errorText';
import LeadFormModal from './LeadFormModal';
import { LEAD_STATUSES, STATUS_TONE, callState, formatCallTime, leadName, sourceLabel } from './leadUi';
import './Leads.css';

const PAGE_SIZE = 20;
// The call centre's default: the leads still in work, the most urgent call first.
const DEFAULTS = { status: 'OPEN', assignedTo: '', due: '', sort: 'nextCall' };
const SEARCH_DEBOUNCE_MS = 300;

function readFilters(params) {
  return {
    status: params.has('status') ? params.get('status') : DEFAULTS.status,
    assignedTo: params.get('owner') || DEFAULTS.assignedTo,
    due: params.get('due') || DEFAULTS.due,
    sort: params.get('sort') || DEFAULTS.sort,
  };
}

/**
 * Leads — the business's sales and call-centre worklist (backend /crm/leads): who asked about a
 * course, who owns them, when to call next. A row opens the lead, where calls are logged and the
 * lead becomes a student. Filters live in the URL so a link ("?due=now") opens the same list.
 */
function Leads() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const isPhone = useMediaQuery('(max-width: 639px)', false);

  const [search, setSearch] = useState(params.get('q') || '');
  const [query, setQuery] = useState(params.get('q') || '');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const generation = useRef(0);

  // Search waits for the typing to stop.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const setFilter = useCallback((key, value) => {
    setPage(1);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const param = key === 'assignedTo' ? 'owner' : key;
      if (value === DEFAULTS[key]) next.delete(param);
      else next.set(param, value);
      return next;
    }, { replace: true });
  }, [setParams]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setQuery('');
    setPage(1);
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const load = useCallback(async () => {
    const mine = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const [list, counts] = await Promise.all([
        getLeads({
          status: filters.status || undefined,
          assignedTo: filters.assignedTo || undefined,
          due: filters.due || undefined,
          sort: filters.sort,
          search: query || undefined,
          page,
          limit: PAGE_SIZE,
        }),
        getLeadSummary(),
      ]);
      if (generation.current !== mine) return;
      setRows(Array.isArray(list?.items) ? list.items : []);
      setMeta(list?.meta || null);
      setSummary(counts || null);
    } catch (err) {
      if (generation.current !== mine) return;
      setError(errorText(err, t('leads.loadError')));
    } finally {
      if (generation.current === mine) setLoading(false);
    }
  }, [filters.status, filters.assignedTo, filters.due, filters.sort, query, page, t]);

  useEffect(() => { load(); }, [load]);

  // The owner filter offers the staff by name; a failure leaves "Mine" and "No owner".
  useEffect(() => {
    let cancelled = false;
    getLeadAssignees().then((list) => { if (!cancelled) setAssignees(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const statusChips = useMemo(() => {
    const counts = summary?.byStatus || {};
    return [
      { value: '', label: t('leads.filters.statusAll'), count: summary?.total },
      { value: 'OPEN', label: t('leads.filters.statusOpen'), count: summary?.open },
      ...LEAD_STATUSES.map((s) => ({ value: s, label: t(`leads.status.${s}`), count: counts[s] })),
    ];
  }, [summary, t]);

  const ownerOptions = useMemo(() => [
    { value: '', label: t('leads.filters.ownerAll') },
    { value: 'me', label: t('leads.filters.ownerMe') },
    { value: 'none', label: t('leads.filters.ownerNone') },
    ...(assignees.length ? [{ separator: true, value: '__sep', label: '' }] : []),
    ...assignees.map((a) => ({ value: a.id, label: a.name })),
  ], [assignees, t]);

  const dueOptions = [
    { value: '', label: t('leads.filters.dueAll') },
    { value: 'now', label: t('leads.filters.dueNow') },
    { value: 'today', label: t('leads.filters.dueToday') },
    { value: 'upcoming', label: t('leads.filters.dueUpcoming') },
    { value: 'unscheduled', label: t('leads.filters.dueUnscheduled') },
  ];
  const sortOptions = [
    { value: 'nextCall', label: t('leads.filters.sortNextCall') },
    { value: 'recent', label: t('leads.filters.sortRecent') },
  ];

  const sheetCount = (filters.assignedTo ? 1 : 0) + (filters.due ? 1 : 0) + (filters.sort !== DEFAULTS.sort ? 1 : 0);
  const filtered = Boolean(query) || filters.status !== DEFAULTS.status || sheetCount > 0;

  const kpis = [
    {
      key: 'dueNow', title: t('leads.kpi.dueNow'), value: summary?.dueNow, hint: t('leads.kpi.dueNowHint'),
      icon: <PhoneCall />, tone: 'danger', active: filters.due === 'now', onClick: () => setFilter('due', filters.due === 'now' ? '' : 'now'),
    },
    {
      key: 'dueToday', title: t('leads.kpi.dueToday'), value: summary?.dueToday, hint: t('leads.kpi.dueTodayHint'),
      icon: <CalendarClock />, tone: 'info', active: filters.due === 'today', onClick: () => setFilter('due', filters.due === 'today' ? '' : 'today'),
    },
    {
      key: 'new', title: t('leads.kpi.newLeads'), value: summary?.byStatus?.NEW, hint: t('leads.kpi.newLeadsHint'),
      icon: <Sparkles />, tone: 'brand', active: filters.status === 'NEW', onClick: () => setFilter('status', filters.status === 'NEW' ? DEFAULTS.status : 'NEW'),
    },
    {
      key: 'conversion', title: t('leads.kpi.conversion'),
      value: summary ? `${summary.conversionRate}%` : undefined,
      hint: t('leads.kpi.conversionHint', { count: summary?.byStatus?.CONVERTED ?? 0 }),
      icon: <TrendingUp />, tone: 'success', active: filters.status === 'CONVERTED',
      onClick: () => setFilter('status', filters.status === 'CONVERTED' ? DEFAULTS.status : 'CONVERTED'),
    },
  ];

  const ownerField = (
    <Dropdown value={filters.assignedTo} onChange={(v) => setFilter('assignedTo', v)} options={ownerOptions} size="sm" ariaLabel={t('leads.filters.owner')} className="leads-select" />
  );
  const dueField = (
    <Dropdown value={filters.due} onChange={(v) => setFilter('due', v)} options={dueOptions} size="sm" ariaLabel={t('leads.filters.due')} className="leads-select" />
  );
  const sortField = (
    <Dropdown value={filters.sort} onChange={(v) => setFilter('sort', v)} options={sortOptions} size="sm" ariaLabel={t('leads.filters.sort')} className="leads-select" />
  );
  const sheetFilters = isPhone ? (
    <>
      <div className="ui-field"><span className="ui-label">{t('leads.filters.owner')}</span>{ownerField}</div>
      <div className="ui-field"><span className="ui-label">{t('leads.filters.due')}</span>{dueField}</div>
      <div className="ui-field"><span className="ui-label">{t('leads.filters.sort')}</span>{sortField}</div>
    </>
  ) : (
    <>{ownerField}{dueField}{sortField}</>
  );

  const appliedOnPhone = isPhone ? [
    filters.assignedTo && { key: 'assignedTo', label: ownerOptions.find((o) => o.value === filters.assignedTo)?.label },
    filters.due && { key: 'due', label: dueOptions.find((o) => o.value === filters.due)?.label },
  ].filter(Boolean) : [];

  const pagination = meta && meta.totalPages > 1 ? (
    <Pagination
      page={meta.page}
      totalPages={meta.totalPages}
      total={meta.total}
      limit={meta.limit || PAGE_SIZE}
      onPrev={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => p + 1)}
      hideOnSinglePage
    />
  ) : null;

  let content;
  if (error) {
    content = <PageEmptyState variant="error" size="sm" title={t('leads.loadError')} description={error !== t('leads.loadError') ? error : undefined} onRetry={load} />;
  } else if (loading && rows.length === 0) {
    content = <LoadingState skeleton="table" rows={6} message={t('common.loading')} />;
  } else if (rows.length === 0) {
    content = filtered ? (
      <PageEmptyState variant="search" size="sm" title={t('leads.noMatchTitle')} description={t('leads.noMatchDesc')} actionLabel={t('leads.filters.clear')} onAction={clearFilters} />
    ) : (
      <PageEmptyState variant="tickets" size="sm" title={t('leads.emptyTitle')} description={t('leads.emptyDesc')} actionLabel={t('leads.add')} onAction={() => setShowForm(true)} />
    );
  } else {
    const now = new Date();
    content = (
      <div className={loading ? 'leads-busy' : undefined} aria-busy={loading || undefined}>
        <TableCard aria-label={t('leads.title')} footer={pagination}>
          <table className="ui-table ui-table--stack leads-table">
            <thead>
              <tr>
                <th>{t('leads.col.lead')}</th>
                <th>{t('leads.col.status')}</th>
                <th>{t('leads.col.nextCall')}</th>
                <th>{t('leads.col.owner')}</th>
                <th className="leads-col-last">{t('leads.col.lastContact')}</th>
                <th className="leads-col-source">{t('leads.col.source')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead) => {
                const state = callState(lead.nextCallAt, now);
                const name = leadName(lead);
                return (
                  <tr key={lead.id} className="leads-row" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <td>
                      <div className="ui-cell-person">
                        <Avatar name={name} id={lead.id} decorative />
                        <div className="ui-cell-person__text">
                          <Link to={`/leads/${lead.id}`} className="ui-cell-person__name leads-name" onClick={(e) => e.stopPropagation()}>
                            {name}
                          </Link>
                          <span className="ui-cell-person__sub">{formatPhone(lead.phoneNumber) || lead.phoneNumber}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label={t('leads.col.status')}>
                      <StatusBadge tone={STATUS_TONE[lead.status] || 'neutral'}>{t(`leads.status.${lead.status}`)}</StatusBadge>
                    </td>
                    <td data-label={t('leads.col.nextCall')}>
                      {lead.status === 'CONVERTED' || lead.status === 'LOST' ? (
                        <span className="leads-muted">—</span>
                      ) : state ? (
                        <span className={`leads-call leads-call--${state}`}>
                          {formatCallTime(t, lead.nextCallAt, now)}
                          {state === 'overdue' && <span className="leads-call__flag">{t('leads.overdue')}</span>}
                        </span>
                      ) : (
                        <span className="leads-muted">{t('leads.notPlanned')}</span>
                      )}
                    </td>
                    <td data-label={t('leads.col.owner')}>
                      {lead.assignedTo ? (
                        <span className="leads-owner">
                          <Avatar name={lead.assignedTo.name} id={lead.assignedTo.id} size="sm" decorative />
                          <span>{lead.assignedTo.name || '—'}</span>
                        </span>
                      ) : (
                        <span className="leads-muted">{t('leads.noOwner')}</span>
                      )}
                    </td>
                    <td data-label={t('leads.col.lastContact')} className="leads-col-last">
                      {lead.lastContactedAt ? formatRelative(lead.lastContactedAt) : <span className="leads-muted">{t('leads.never')}</span>}
                    </td>
                    <td data-label={t('leads.col.source')} className="leads-col-source">{sourceLabel(t, lead.source)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      </div>
    );
  }

  return (
    <div className="leads-page">
      <PageHead
        icon={<UserPlus />}
        title={t('leads.title')}
        description={t('leads.subtitle')}
        actions={(
          <button type="button" className="ds-btn ds-btn-primary" onClick={() => setShowForm(true)}>
            <Plus aria-hidden="true" />
            {t('leads.add')}
          </button>
        )}
      />

      <div className="kpi-row leads-kpis">
        {kpis.map((k) => (
          <MetricCard
            key={k.key}
            title={k.title}
            value={k.value ?? '—'}
            change={k.hint}
            icon={k.icon}
            tone={k.tone}
            loading={!summary && !error}
            active={k.active}
            onClick={k.onClick}
          />
        ))}
      </div>

      <div className="ui-chip-row leads-status" role="group" aria-label={t('leads.filters.status')}>
        {statusChips.map((chip) => (
          <button
            key={chip.value || 'all'}
            type="button"
            className="ui-chip"
            aria-pressed={filters.status === chip.value}
            onClick={() => setFilter('status', chip.value)}
          >
            {chip.label}
            {chip.count != null && <span className="ui-count">{chip.count}</span>}
          </button>
        ))}
      </div>

      <div className="leads-toolbar">
        <SearchInput
          className="leads-search"
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder={t('leads.searchPlaceholder')}
          aria-label={t('leads.searchPlaceholder')}
        />
        <FilterSheet count={sheetCount} onClear={() => { setFilter('assignedTo', ''); setFilter('due', ''); setFilter('sort', DEFAULTS.sort); }}>
          {sheetFilters}
        </FilterSheet>
      </div>

      {appliedOnPhone.length > 0 && (
        <div className="leads-applied">
          {appliedOnPhone.map((f) => (
            <span key={f.key} className="ui-chip ui-chip--removable">
              <span>{f.label}</span>
              <button type="button" className="ui-chip__x" onClick={() => setFilter(f.key, '')} aria-label={`${t('leads.filters.clear')}: ${f.label}`}>
                <X aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      {content}

      {showForm && (
        <LeadFormModal
          assignees={assignees}
          onClose={() => setShowForm(false)}
          onSaved={(lead) => {
            setShowForm(false);
            if (lead?.id) navigate(`/leads/${lead.id}`);
            else load();
          }}
        />
      )}
    </div>
  );
}

export default Leads;
